import { Box } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

const StyledGeneralTableBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'stickyHeader' && prop !== 'showHover' && prop !== 'hasMaxHeight' && prop !== 'showZebra',
})(({ theme, stickyHeader, showHover, hasMaxHeight, showZebra }) => ({
  ...(hasMaxHeight
    ? {
        '--general-table-max-height': 'calc(100vh - 340px)',
        maxHeight: 'var(--general-table-max-height)',
        [theme.breakpoints.down('sm')]: {
          '--general-table-max-height': 'calc(100vh - 240px)',
          '.MuiDialogContent-root &': {
            '--general-table-max-height': '55vh',
          },
        },
        '.MuiDialogContent-root &': {
          '--general-table-max-height': '62vh',
        },
      }
    : {}),
  display: 'flex',
  width: '100%',
  overflowX: 'auto',
  overflowY: 'auto',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  backgroundColor: theme.palette.background.formTables,
  '& table': {
    tableLayout: 'auto',
    width: 'auto',
    minWidth: 'max-content',
    borderCollapse: stickyHeader ? 'separate' : undefined,
    borderSpacing: stickyHeader ? 0 : undefined,
  },
  '& th, & td': {
    whiteSpace: 'nowrap',
    width: 'auto',
  },
  '& :where(tbody > tr)': {
    transition: 'background-color 120ms ease',
  },
  '& :where(tbody > tr:nth-of-type(odd):not(.Mui-selected))': {
    backgroundColor: showZebra ? theme.palette.background.paper : undefined,
  },
  '& :where(tbody > tr:nth-of-type(even):not(.Mui-selected))': {
    backgroundColor: showZebra ? alpha(theme.palette.common.black, 0.055) : undefined,
  },
  '& tbody > tr:hover:not(.Mui-selected)': {
    backgroundColor: showHover
      ? `${alpha(theme.palette.info.main, 0.18)} !important`
      : undefined,
  },
  ...(stickyHeader
    ? {
        '& thead th': {
          position: 'sticky',
          top: 0,
          zIndex: 3,
          background: `linear-gradient(to bottom, ${theme.palette.primary.main} 95%, #b0b0b0 96%, #f0f0f0 100%)`,
          backgroundClip: 'padding-box',
        },
      }
    : {}),
  '*::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '*::-webkit-scrollbar-track': {
    background: 'inherit',
    boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
  },
  '*::-webkit-scrollbar-thumb': {
    backgroundColor: 'gray',
    borderRadius: '20px',
  },
  '*::-webkit-scrollbar-corner': {
    background: 'inherit',
  },
}));

const normalizeDimension = (value) => (typeof value === 'number' ? `${value}px` : value);

const GeneralTableBox = ({ children, sx = {}, stickyHeader = true, showHover = true, hasMaxHeight = true, showZebra = true, maxHeight, ...props }) => {
  const combinedSx = {
    ...(maxHeight !== undefined
      ? {
          '--general-table-max-height': normalizeDimension(maxHeight),
        }
      : {}),
    ...sx,
  };

  return (
    <StyledGeneralTableBox
      stickyHeader={stickyHeader}
      showHover={showHover}
      hasMaxHeight={hasMaxHeight}
      showZebra={showZebra}
      sx={combinedSx}
      {...props}
    >
      {children}
    </StyledGeneralTableBox>
  );
};

export default GeneralTableBox;
